import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface RefundPolicyProps {
  onBack: () => void;
}

export const RefundPolicy: React.FC<RefundPolicyProps> = ({ onBack }) => {
  useSEOMetaTags({
    title: 'Refund Policy - Orcaseotools | Return & Refund Terms',
    description: 'Refund Policy for Orcaseotools SEO and AEO audit tools. Learn about our 14-day refund policy, return process, and eligibility requirements for our digital services.',
    keywords: 'refund policy, return policy, money back guarantee, SEO tools refund, website audit refund, Orcaseotools refund',
    canonicalUrl: 'https://orcaseotools.com/refund-policy',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Refund Policy - Orcaseotools",
      "description": "Refund Policy for Orcaseotools SEO and AEO audit tools",
      "url": "https://orcaseotools.com/refund-policy",
      "inLanguage": "en-US"
    }
  });

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-6">
          <header>
            <h1 className="text-4xl font-bold mb-2">Return and Refund Policy</h1>
            <p className="text-slate-400 text-sm">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </header>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">1. Overview</h2>
            <p className="text-slate-300 leading-relaxed">
              At Orcaseotools, operated by Sofia Host LLC, we want you to be completely satisfied with your purchase. This policy outlines the conditions under which refunds are provided for our digital SEO and AEO audit tools and services.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">2. 14-Day Money-Back Guarantee</h2>
            <p className="text-slate-300 leading-relaxed">
              We offer a 14-Day Money-Back Guarantee for all our subscription plans. If you are not satisfied with our service, you can request a full refund within 14 days of your initial purchase, subject to the eligibility criteria outlined below.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">3. Eligibility Criteria</h2>
            <p className="text-slate-300 leading-relaxed">
              To be eligible for a refund under our 14-Day Money-Back Guarantee, you must meet the following conditions:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Must request the refund within 14 days of the initial purchase.</li>
              <li>Your subscription must not have been used extensively (no more than 50% of your allocated credits consumed).</li>
              <li>The request must be your first-time refund request with us.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">4. Non-Refundable Items</h2>
            <p className="text-slate-300 leading-relaxed">
              The following purchases and items are strictly non-refundable:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Consulting sessions that have already been completed.</li>
              <li>Credits or plans that have been fully consumed.</li>
              <li>Subscriptions past the 14-day refund window.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">5. How to Request a Refund</h2>
            <p className="text-slate-300 leading-relaxed">
              To initiate a refund request, you can choose one of the following methods:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Email us at <strong>contact@orcaseotools.com</strong> with your order details.</li>
              <li>Contact us via WhatsApp at <strong>+1 (606) 557-7288</strong>.</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-2">
              Please include your account email, purchase date, and reason for the refund in your request to help us process it quickly.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">6. Refund Processing</h2>
            <p className="text-slate-300 leading-relaxed">
              Once your refund request is approved, we will process it within 5-10 business days. Refunds are issued directly to the original payment method used during purchase via Stripe. Please note that Stripe processing fees are non-refundable.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">7. Partial Refunds</h2>
            <p className="text-slate-300 leading-relaxed">
              For annual plans, we may offer partial, prorated refunds on a case-by-case basis if requested after the initial 14-day window, minus the cost of the months already utilized and any applicable fees.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">8. Chargebacks</h2>
            <p className="text-slate-300 leading-relaxed">
              We encourage you to contact us first to resolve any billing issues before filing a chargeback or dispute with your bank. Unwarranted chargebacks may result in the immediate suspension or termination of your account.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">9. Contact Information</h2>
            <p className="text-slate-300 leading-relaxed">
              If you have any questions or need further assistance regarding our Return and Refund Policy, please reach out to us:
            </p>
            <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
              <p className="text-slate-300"><strong>Company:</strong> Sofia Host LLC</p>
              <p className="text-slate-300"><strong>Address:</strong> 1209 MOUNTAIN ROAD PL NE STE R, ALBUQUERQUE, NM, 87110, USA</p>
              <p className="text-slate-300"><strong>Email:</strong> contact@orcaseotools.com</p>
              <p className="text-slate-300"><strong>Phone (WhatsApp):</strong> +1 (606) 557-7288</p>
            </div>
          </section>

          <div className="pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Thank you for choosing <strong>Orcaseotools</strong>. Your satisfaction is our priority, and we strive to make the refund process as smooth as possible for our valued customers.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};
